import React, { useContext, useEffect, useState } from "react";
import {
  makeStyles,
  Button,
  Typography,
  FormGroup,
  FormControlLabel,
  Checkbox,
} from "@material-ui/core";
import { FieldArray, ErrorMessage } from "formik";
import StarIcon from "@material-ui/icons/Star";
import Select from "react-select";
import NewCaseField from "./Fields/NewCaseField";
import NewCaseSelect from "./Fields/NewCaseSelect";
import MaskField from "./Fields/MaskField";

import RelationDialog from "../../components/RelationDialog";
import FieldsDialog from "../../components/FieldsDialog";
import SocialDialog from "../../components/SocialDialog";
import FormError from "../../components/FormError";
import OpenDialogIcon from "../../icons/OpenDialog";
import NewCaseIcon from "../../icons/NewCaseIcon";

import { getSNNMask, targetUrl } from "../../components/shared/helpers/helper";
import { states } from "../shared/helpers/states";
import { GENDERS, MARITAL_STATUS, SUBJECT_LOGOS } from "../../utils/constants";
import { viewportContext } from "../../context/ViewportContext";
import { useCaseStyles } from "./useCaseStyle";
import { reactSelectStyles } from "./reactSelectStyles";
import {
  createNewSubject,
  relatedSubjectIdFor,
} from "../shared/helpers/newCaseInitValues";
import SubjectTypeToggle from "../shared/SubjectTypeToggle";
import { tokens } from "../../theme/tokens";
import { SubjectType } from "../../types/CaseTypes";

import {
  AddressInfoColumnProps,
  DeceasedInfoSectionProps,
  DialogButtonFieldProps,
  ExtendedDeceasedInfoProps,
  NewCaseSubjectFormProps,
  PersonalInfoColumnProps,
  SocialMediaLinksFieldProps,
  SubjectNavigationButtonsProps,
} from "../../types/NewCaseView/NewCaseSubjectFormTypes";

const useStyles = makeStyles(() => ({
  parentChild: {
    flex: 1,
    minWidth: 0,
  },
  parentChildTallScreen: { flex: 1, width: "100%", minWidth: 0 },
}));

// Sub-component: Subject Navigation Buttons
const SubjectNavigationButtons = ({
  subjects,
  currentIndex,
  changeIndex,
}: SubjectNavigationButtonsProps) => {
  const useCaseClasses = useCaseStyles();

  return (
    <div className={useCaseClasses.buttonContainer}>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          columnGap: "10px",
          rowGap: "10px",
        }}
      >
        {subjects.map((subject, i) => (
          <Button
            key={i}
            onClick={() => changeIndex(i)}
            className={`${useCaseClasses.subjectButton} ${
              i === currentIndex
                ? useCaseClasses.subjectButtonActive
                : useCaseClasses.subjectButtonInactive
            }`}
          >
            {i === 0 ? "Primary Subject" : `Subject ${i}`}
            {subject.is_primary && (
              <StarIcon className={useCaseClasses.subjectIcon} />
            )}
          </Button>
        ))}
      </div>
      <br />
      <div>
        <FieldArray name="case_details.Subjects_information">
          {({ push }) => (
            <Button
              endIcon={<NewCaseIcon style={{ overflow: "visible" }} />}
              className={useCaseClasses.addSubjectButton}
              onClick={() => {
                push(createNewSubject(false, relatedSubjectIdFor(subjects)));
              }}
            >
              Add Subject
            </Button>
          )}
        </FieldArray>
      </div>
    </div>
  );
};

// Sub-component: Dialog Button Field
const DialogButtonField = ({
  label,
  onClick,
  tallScreen,
}: DialogButtonFieldProps) => {
  const classes = useStyles();
  const useCaseClasses = useCaseStyles();

  return (
    <div className={useCaseClasses.parent}>
      <div
        className={
          tallScreen ? classes.parentChildTallScreen : classes.parentChild
        }
      >
        <div className={useCaseClasses.rowContainer}>
          <div className={useCaseClasses.labelContainer}>
            <Typography>{label}</Typography>
          </div>
          <div
            className={useCaseClasses.fieldContainer}
            style={{
              display: "flex",
              alignItems: "center",
              columnGap: "9px",
            }}
          >
            <Button style={{ width: "40px", height: "40px" }} onClick={onClick}>
              <OpenDialogIcon />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sub-component: Social Media Links Field
const SocialMediaLinksField = ({
  socialMedia,
  onClick,
  tallScreen,
}: SocialMediaLinksFieldProps) => {
  const classes = useStyles();
  const useCaseClasses = useCaseStyles();

  return (
    <div className={useCaseClasses.parent}>
      <div
        className={
          tallScreen ? classes.parentChildTallScreen : classes.parentChild
        }
      >
        <div className={useCaseClasses.rowContainer}>
          <div className={useCaseClasses.labelContainer}>
            <Typography>Social Media links</Typography>
          </div>
          <div
            className={useCaseClasses.fieldContainer}
            style={{
              display: "flex",
              alignItems: "center",
              columnGap: "9px",
            }}
          >
            {socialMedia?.map((val, key) => {
              if (key > 4) return null;
              return (
                <span
                  key={key}
                  onClick={() => targetUrl(val.value)}
                  style={
                    val.value === ""
                      ? { display: "none" }
                      : { cursor: "pointer" }
                  }
                >
                  <img
                    src={SUBJECT_LOGOS[key]}
                    style={{ height: "32px" }}
                    alt={val.value}
                  />
                </span>
              );
            })}
            <Button style={{ width: "40px", height: "40px" }} onClick={onClick}>
              <OpenDialogIcon />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Sub-component: Deceased Information Section
const DeceasedInfoSection = ({
  index,
  deceasedState,
}: DeceasedInfoSectionProps) => {
  const useCaseClasses = useCaseStyles();

  if (!deceasedState) return null;

  return (
    <div>
      <div className={useCaseClasses.parent}>
        <NewCaseField
          label={"Date of death"}
          type={"date"}
          disabled={!deceasedState}
          name={`case_details.Subjects_information[${index}].date_of_death`}
        />
      </div>
      <div className={useCaseClasses.parent}>
        <NewCaseField
          label={"Death certificate order date"}
          type={"date"}
          disabled={!deceasedState}
          name={`case_details.Subjects_information[${index}].death_cert_order_date`}
        />
      </div>
      <div className={useCaseClasses.parent}>
        <NewCaseField
          label={"Death certificate released date"}
          type={"date"}
          disabled={!deceasedState}
          name={`case_details.Subjects_information[${index}].death_cert_received_date`}
        />
      </div>
    </div>
  );
};

// Sub-component: Extended Deceased Information
const ExtendedDeceasedInfo = ({
  index,
  deceasedState,
}: ExtendedDeceasedInfoProps) => {
  const useCaseClasses = useCaseStyles();

  if (!deceasedState) return null;

  return (
    <div>
      <div className={useCaseClasses.parent}>
        <NewCaseField
          label={"Place of death"}
          type={"text"}
          disabled={!deceasedState}
          name={`case_details.Subjects_information[${index}].place_of_death`}
        />
      </div>
      <div className={useCaseClasses.parent}>
        <NewCaseField
          label={"Father name"}
          type={"text"}
          disabled={!deceasedState}
          name={`case_details.Subjects_information[${index}].father_name`}
        />
      </div>
      <div className={useCaseClasses.parent}>
        <NewCaseField
          label={"Mother name"}
          disabled={!deceasedState}
          type={"text"}
          name={`case_details.Subjects_information[${index}].mother_name`}
        />
      </div>
    </div>
  );
};

// Sub-component: Left Column (Personal Info)
const PersonalInfoColumn = ({
  index,
  subject,
  deceasedState,
  setDeceasedState,
  setFieldValue,
  setDialogOpen,
  setPhoneDialog,
  setEmailDialog,
  tallScreen,
}: PersonalInfoColumnProps) => {
  const useCaseClasses = useCaseStyles();
  const isBusiness = subject.subject_type === "business";

  return (
    <div style={{ flex: "1" }}>
      {isBusiness ? (
        <div className={useCaseClasses.parent}>
          <NewCaseField
            label={"Business name"}
            type={"text"}
            name={`case_details.Subjects_information[${index}].business_name`}
            required={true}
          />
        </div>
      ) : (
        <PersonNameFields index={index} />
      )}

      <SocialMediaLinksField
        socialMedia={subject.social_media}
        onClick={() => setDialogOpen(true)}
        tallScreen={tallScreen}
      />

      <DialogButtonField
        label="Phone Number"
        onClick={() => setPhoneDialog(true)}
        tallScreen={tallScreen}
      />

      <DialogButtonField
        label="Email"
        onClick={() => setEmailDialog(true)}
        tallScreen={tallScreen}
      />

      {!isBusiness && (
        <>
          <div className={useCaseClasses.parent}>
            <FormGroup>
              <FormControlLabel
                style={{ color: tokens.color.text.primary }}
                control={
                  <Checkbox
                    checked={deceasedState}
                    onChange={() => {
                      setDeceasedState(!deceasedState);
                      setFieldValue(
                        `case_details.Subjects_information[${index}].deceased`,
                        !deceasedState
                      );
                    }}
                    style={{ color: tokens.color.brand.primary }}
                  />
                }
                label={"Click the checkmark if the person is deceased"}
              />
            </FormGroup>
          </div>

          <DeceasedInfoSection index={index} deceasedState={deceasedState} />
        </>
      )}
    </div>
  );
};

// Sub-component: Person name, gender and date of birth
const PersonNameFields = ({ index }: { index: number }) => {
  const useCaseClasses = useCaseStyles();

  return (
    <>
      <div className={useCaseClasses.parent}>
        <NewCaseField
          label={"First name"}
          type={"text"}
          name={`case_details.Subjects_information[${index}].first_name`}
          required={true}
        />
      </div>
      <div className={useCaseClasses.parent}>
        <NewCaseField
          label={"Middle name"}
          type={"text"}
          name={`case_details.Subjects_information[${index}].middle_name`}
        />
      </div>
      <div className={useCaseClasses.parent}>
        <NewCaseField
          label={"Last name"}
          type={"text"}
          name={`case_details.Subjects_information[${index}].last_name`}
          required={true}
        />
      </div>
      <div className={useCaseClasses.parent}>
        <NewCaseSelect
          label={"Gender"}
          name={`case_details.Subjects_information[${index}].gender`}
          options={GENDERS}
        />
      </div>
      <div className={useCaseClasses.parent}>
        <NewCaseField
          label={"Date of birth"}
          type={"date"}
          name={`case_details.Subjects_information[${index}].dob`}
        />
      </div>
    </>
  );
};

// Sub-component: Right Column (Address and Additional Info)
const AddressInfoColumn = ({
  index,
  subject,
  deceasedState,
  setFieldValue,
  setRDialog,
  tallScreen,
}: AddressInfoColumnProps) => {
  const classes = useStyles();
  const useCaseClasses = useCaseStyles();

  return (
    <div style={{ flex: "1" }}>
      <div className={useCaseClasses.parent}>
        <NewCaseField
          label={"Street"}
          type={"text"}
          name={`case_details.Subjects_information[${index}].street_address`}
        />
      </div>
      <div className={useCaseClasses.parent}>
        <NewCaseField
          label={"City"}
          type={"text"}
          name={`case_details.Subjects_information[${index}].city`}
        />
      </div>
      <div className={useCaseClasses.parent}>
        <div
          className={
            tallScreen ? classes.parentChildTallScreen : classes.parentChild
          }
        >
          <div className={useCaseClasses.rowContainer}>
            <div className={useCaseClasses.labelContainer}>
              <Typography>
                State
              </Typography>
            </div>
            <div className={useCaseClasses.fieldContainer}>
              <Select
                styles={reactSelectStyles}
                value={{ label: subject.state }}
                onChange={(e: any) => {
                  setFieldValue(
                    `case_details.Subjects_information[${index}].state`,
                    e.value
                  );
                }}
                options={states}
              />
            </div>
          </div>
          <ErrorMessage
            name={`case_details.Subjects_information[${index}].state`}
            component={FormError}
            className={useCaseClasses.error}
          />
        </div>
      </div>
      <div className={useCaseClasses.parent}>
        <NewCaseField
          label={"Zip-code"}
          type={"text"}
          name={`case_details.Subjects_information[${index}].zip_code`}
        />
      </div>
      {subject.subject_type !== "business" && (
        <>
          <div className={useCaseClasses.parent}>
            <MaskField
              label={"SNN"}
              mask={getSNNMask()}
              setFieldValue={setFieldValue}
              name={`case_details.Subjects_information[${index}].ssn`}
              value={subject.ssn}
            />
          </div>
          <div className={useCaseClasses.parent}>
            <NewCaseSelect
              label={"Marital Status"}
              name={`case_details.Subjects_information[${index}].marital_status`}
              options={MARITAL_STATUS}
            />
          </div>

          <DialogButtonField
            label="Relationship"
            onClick={() => setRDialog(true)}
            tallScreen={tallScreen}
          />

          {index !== 0 && (
            <div className={useCaseClasses.parent}>
              <div
                className={
                  tallScreen ? classes.parentChildTallScreen : classes.parentChild
                }
              >
                <div className={useCaseClasses.rowContainer}>
                  <div className={useCaseClasses.labelContainer}>
                    <Typography></Typography>
                  </div>
                  <div className={useCaseClasses.fieldContainer}></div>
                </div>
              </div>
            </div>
          )}

          {index === 0 && (
            <div className={useCaseClasses.parent}>
              <FormGroup>
                <FormControlLabel
                  style={{ color: tokens.color.surface.default }}
                  control={
                    <Checkbox style={{ color: tokens.color.surface.default }} />
                  }
                  label={undefined}
                />
              </FormGroup>
            </div>
          )}

          <ExtendedDeceasedInfo index={index} deceasedState={deceasedState} />
        </>
      )}
    </div>
  );
};

// Main Component
const NewCaseSubjectForm = ({
  subjects,
  currentIndex,
  changeIndex,
  setFieldValue,
  errors,
}: NewCaseSubjectFormProps) => {
  const useCaseClasses = useCaseStyles();
  const { tallScreen } = useContext(viewportContext) as {
    tallScreen?: boolean;
  };

  const currentSubject = subjects[currentIndex];

  const [deceasedState, setDeceasedState] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [phoneDialog, setPhoneDialog] = useState<boolean>(false);
  const [emailDialog, setEmailDialog] = useState<boolean>(false);
  const [rDialog, setRDialog] = useState<boolean>(false);
  const [primaryState, setPrimaryState] = useState<boolean>(false);
  const [relationshipOptions, setRelationshipOptions] = useState<
    { label: string; value: number }[]
  >([]);

  useEffect(() => {
    if (!currentSubject) return;

    setDeceasedState(
      currentSubject.subject_type !== "business" && currentSubject.deceased
    );
    setPrimaryState(currentSubject.is_primary);

    const getRelationshipOptions = (): { label: string; value: number }[] => {
      // Businesses take no part in relationships.
      return subjects.flatMap((val, key) =>
        key === currentIndex || val.subject_type === "business"
          ? []
          : [{ label: val.first_name, value: key }]
      );
    };
    setRelationshipOptions(getRelationshipOptions());
  }, [currentIndex, currentSubject, subjects]);

  // Early return if no valid subject
  if (!currentSubject || !subjects.length) {
    return (
      <div style={{ textAlign: "center" }}>
        <Typography>No subject data available</Typography>
      </div>
    );
  }

  // Only flips subject_type, so a misclick loses nothing; submit strips the rest.
  const handleTypeChange = (type: SubjectType): void => {
    const path = `case_details.Subjects_information[${currentIndex}]`;
    setFieldValue(`${path}.subject_type`, type, false);
    if (type === "business") {
      setFieldValue(
        `${path}.business_name`,
        (currentSubject as { business_name?: string }).business_name ?? "",
        false
      );
    }
  };

  const handlePrimaryState = (): void => {
    if (!primaryState) {
      setPrimaryState(true);
      setFieldValue(
        `case_details.Subjects_information[${currentIndex}].is_primary`,
        true,
        false
      );
    } else {
      setPrimaryState(false);
      setFieldValue(
        `case_details.Subjects_information[${currentIndex}].is_primary`,
        false,
        false
      );

      // Remove relationships pointing to this subject
      subjects.forEach((subject, itemIndex) => {
        if (subject.subject_type === "business") return;
        setFieldValue(
          `case_details.Subjects_information[${itemIndex}].relationship`,
          subject.relationship.filter(
            (val) => val.subjectIndex !== currentIndex
          ),
          false
        );
      });
    }
  };

  return (
    <div>
      <SubjectNavigationButtons
        subjects={subjects}
        currentIndex={currentIndex}
        changeIndex={changeIndex}
      />

      <div className={useCaseClasses.subjectHeader}>
        <div className={useCaseClasses.subjectTypeField}>
          <Typography>Subject type</Typography>
          <SubjectTypeToggle
            value={
              currentSubject.subject_type === "business" ? "business" : "person"
            }
            onChange={handleTypeChange}
          />
        </div>
        {currentIndex !== 0 && (
          <FormGroup>
            <FormControlLabel
              className={useCaseClasses.primaryCheckbox}
              control={
                <Checkbox
                  checked={primaryState}
                  onChange={handlePrimaryState}
                  style={{ color: tokens.color.brand.primaryAction }}
                />
              }
              label="Make this a primary subject"
            />
          </FormGroup>
        )}
      </div>

      <div className={useCaseClasses.columns}>
        <PersonalInfoColumn
          index={currentIndex}
          subject={currentSubject}
          deceasedState={deceasedState}
          setDeceasedState={setDeceasedState}
          setFieldValue={setFieldValue}
          setDialogOpen={setDialogOpen}
          setPhoneDialog={setPhoneDialog}
          setEmailDialog={setEmailDialog}
          tallScreen={tallScreen}
        />

        <AddressInfoColumn
          index={currentIndex}
          subject={currentSubject}
          deceasedState={deceasedState}
          setFieldValue={setFieldValue}
          setRDialog={setRDialog}
          tallScreen={tallScreen}
        />
      </div>

      <RelationDialog
        rDialog={rDialog}
        setRDialog={setRDialog}
        relationshipOptions={relationshipOptions}
        index={currentIndex}
        data={subjects}
        arrayName={"case_details.Subjects_information"}
        setFieldValue={setFieldValue}
      />
      <FieldsDialog
        dialogOpen={emailDialog}
        handleDialogClose={() => setEmailDialog(false)}
        title={"Add or update Email"}
        data={currentSubject.email}
        arrayName={`case_details.Subjects_information[${currentIndex}].email`}
      />
      <FieldsDialog
        dialogOpen={phoneDialog}
        handleDialogClose={() => setPhoneDialog(false)}
        title={"Add or update Phone Number"}
        data={currentSubject.phone}
        arrayName={`case_details.Subjects_information[${currentIndex}].phone`}
      />
      <SocialDialog
        dialogOpen={dialogOpen}
        data={currentSubject.social_media}
        arrayName={`case_details.Subjects_information[${currentIndex}].social_media`}
        handleDialogClose={() => setDialogOpen(false)}
        index={currentIndex}
      />
    </div>
  );
};

export default NewCaseSubjectForm;
